import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EinstellungenProvider } from './contexts/EinstellungenContext';
import Layout from './components/Layout';
import NoAccess from './components/NoAccess';
import { mayAccess, rollenFuer } from './routeAccess';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InstanzZugangPage from './pages/InstanzZugangPage';
import DashboardPage from './pages/DashboardPage';
import MonitoringPage from './pages/MonitoringPage';
import BereitschaftPage from './pages/BereitschaftPage';
import GitRegistryPage from './pages/GitRegistryPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SicherungszielePage from './pages/SicherungszielePage';
import ProductsPage from './pages/ProductsPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ServersPage from './pages/ServersPage';
import DomainsPage from './pages/DomainsPage';
import DomainUrlsPage from './pages/DomainUrlsPage';
import FeedbackBriefkastenPage from './pages/FeedbackBriefkastenPage';
import InferencePage from './pages/InferencePage';
import VerbundPage from './pages/VerbundPage';
import InstancesPage from './pages/InstancesPage';
import InstanceDetailPage from './pages/InstanceDetailPage';
import OrdersPage from './pages/OrdersPage';
import CustomersPage from './pages/CustomersPage';
import DeploymentsPage from './pages/DeploymentsPage';
import ReleasesPage from './pages/ReleasesPage';
import AuditPage from './pages/AuditPage';
import IntegrationsPage from './pages/IntegrationsPage';
import FmMigrationPage from './pages/FmMigrationPage';
import ApiKeysPage from './pages/ApiKeysPage';
import UsersPage from './pages/UsersPage';
import EmbedKeysPage from './pages/EmbedKeysPage';
import BaseStacksPage from './pages/BaseStacksPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check for the restricted pages. Looked up by path rather than passed
  // per route, so adding an entry to ROUTE_ROLES is enough — there is no second
  // place to remember. Rendered inside Layout so the user keeps their bearings
  // instead of being bounced somewhere unexplained.
  //
  // Ueber `mayAccess` und nicht mehr ueber den blossen Schluesselzugriff: die
  // Tabelle fuehrt Muster (`/instances/:id`), der Browser liefert Pfade. Ein
  // direkter Zugriff findet dazwischen nichts, und `/domains/:id/urls` stand
  // deshalb zwar in der Tabelle, hat aber nie gegriffen.
  if (!mayAccess(location.pathname, user?.role)) {
    return (
      <Layout>
        <NoAccess required={rollenFuer(location.pathname) ?? []} />
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Ohne ProtectedRoute, und das ist der Zweck: der Empfänger dieser Seite
          hat noch keinen Zugang. Die Berechtigung trägt das Token in der URL. */}
      <Route path="/instanz-zugang" element={<InstanzZugangPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitoring"
        element={
          <ProtectedRoute>
            <MonitoringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bereitschaft"
        element={
          <ProtectedRoute>
            <BereitschaftPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/git-registry"
        element={
          <ProtectedRoute>
            <GitRegistryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sicherungsziele"
        element={
          <ProtectedRoute>
            <SicherungszielePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applications/:id"
        element={
          <ProtectedRoute>
            <ApplicationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <ProtectedRoute>
            <ProductDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/servers"
        element={
          <ProtectedRoute>
            <ServersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains"
        element={
          <ProtectedRoute>
            <DomainsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains/:id/urls"
        element={
          <ProtectedRoute>
            <DomainUrlsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <FeedbackBriefkastenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inference"
        element={
          <ProtectedRoute>
            <InferencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verbund"
        element={
          <ProtectedRoute>
            <VerbundPage />
          </ProtectedRoute>
        }
      />
      {/* Zwei weitere Sichten auf dieselbe Liste. Siehe
          docs/KONZEPT-APPS-UND-WEBSEITEN.md. */}
      <Route
        path="/fm-migration"
        element={
          <ProtectedRoute>
            <FmMigrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apps"
        element={
          <ProtectedRoute>
            <InstancesPage key="APP" art="APP" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webseiten"
        element={
          <ProtectedRoute>
            <InstancesPage key="WEBSEITE" art="WEBSEITE" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instances"
        element={
          <ProtectedRoute>
            <InstancesPage key="PRODUKT" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/instances/:id"
        element={
          <ProtectedRoute>
            <InstanceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deployments"
        element={
          <ProtectedRoute>
            <DeploymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/releases"
        element={
          <ProtectedRoute>
            <ReleasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/embed-keys"
        element={
          <ProtectedRoute>
            <EmbedKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/base-stacks"
        element={
          <ProtectedRoute>
            <BaseStacksPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <EinstellungenProvider>
        <AppRoutes />
      </EinstellungenProvider>
    </AuthProvider>
  );
}
